import { ReactNode, useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Header } from './Header';
import SessionTimeoutModal from '../SessionTimeoutModal';
import SessionDebugInfo from '../SessionDebugInfo';
import { SESSION_WARNING_EVENT, SESSION_CONTINUE_EVENT } from '../../utils/sessionManager';

type LayoutProps = {
  children: ReactNode;
};

const Layout = ({ children }: LayoutProps) => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [warningDetails, setWarningDetails] = useState({
    warningTimeMS: 0,
    logoutTimeMS: 0
  });
  
  // Listen for session timeout warning
  useEffect(() => {
    const handleSessionWarning = (event: CustomEvent) => {
      setWarningDetails(event.detail);
      setShowTimeoutWarning(true);
    };
    
    // Add event listener for session warning
    window.addEventListener(
      SESSION_WARNING_EVENT, 
      handleSessionWarning as EventListener
    );
    
    return () => {
      window.removeEventListener(
        SESSION_WARNING_EVENT,
        handleSessionWarning as EventListener
      );
    };
  }, []);
  
  // Handle continue session
  const handleContinueSession = () => {
    setShowTimeoutWarning(false);
    // Dispatch continue event to reset the timer
    window.dispatchEvent(new CustomEvent(SESSION_CONTINUE_EVENT));
  };
  
  // Toggle debug panel with keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebugInfo(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col ml-0 lg:ml-64">
        <Header />
        
        <main className="flex-1 p-4 lg:p-6 overflow-auto no-scrollbar">
          <div className="w-full max-w-7xl mx-auto no-scrollbar">
            {children}
          </div>
        </main>
      </div>
      
      {/* Session timeout warning modal */}
      <SessionTimeoutModal
        warningTimeMS={warningDetails.warningTimeMS}
        logoutTimeMS={warningDetails.logoutTimeMS}
        onContinue={handleContinueSession}
        isVisible={showTimeoutWarning}
      />
      
      {/* Debug panel (toggle with Ctrl+Shift+D) */}
      <SessionDebugInfo isVisible={showDebugInfo} />
    </div>
  );
};

export default Layout; 